import { StylePropertyDefinition } from '../../types/common';

/**
 * The chat's palette, as theme variables.
 *
 * PromptStyle.tsx hard-codes its own greys, its own font stack and a white
 * surface, which is fine for a full-page AI screen and wrong everywhere else:
 * embedded in a builder panel it reads as a foreign white box. These rules are
 * appended AFTER the base stylesheet by `processStyleDefinition`, so declaring a
 * property here overrides the literal without touching 2,400 lines of layout.
 *
 * Every default is the literal the component ships today, so an app that sets
 * none of these looks exactly as it did. Theming is opt-in per key.
 *
 * The chat's text colors resolve from its own keys (promptFontColor /
 * promptSecondaryFontColor / promptTertiaryFontColor), not from the app-wide
 * palette (fontColorOne/Two/Three). The adzump theme sets fontColorTwo to
 * #FFFFFF for its dark marketing surfaces, which made every secondary line in
 * the chat white-on-white (live 2026-09-10). Two rules still read the app
 * palette and should move over when next touched: the expired attachment chip
 * (fontColorThree) and the steer button (fontColorOne).
 */
export const styleProperties: Array<StylePropertyDefinition> = [
	// ─── Text tones (no sel/cp: resolved inline by PromptStyle's t(), declared
	// here so the Theme Editor lists them) ───
	{
		gn: 'Prompt Surface',
		dn: 'Secondary Font Color',
		de: 'Labels, tool rows, craft-panel entries - all mid-emphasis text',
		n: 'promptSecondaryFontColor',
	},
	{
		gn: 'Prompt Surface',
		dn: 'Tertiary Font Color',
		de: 'Timestamps, durations, muted hints - all low-emphasis text',
		n: 'promptTertiaryFontColor',
	},
	// ─── Shell ───
	{
		gn: 'Prompt Surface',
		dn: 'Prompt Background',
		n: 'promptBackground',
		dv: '#ffffff',
		cp: 'background',
		sel: '.comp.compPrompt',
		np: true,
	},
	{
		gn: 'Prompt Surface',
		dn: 'Prompt Font Family',
		n: 'promptFontFamily',
		dv: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
		cp: 'font-family',
		sel: '.comp.compPrompt',
		np: true,
	},
	{
		gn: 'Prompt Surface',
		dn: 'Prompt Font Color',
		n: 'promptFontColor',
		dv: '#1a1a1a',
		cp: 'color',
		sel: '.comp.compPrompt',
		np: true,
	},
	{
		gn: 'Prompt Surface',
		dn: 'Prompt Border Color',
		n: 'promptBorderColor',
		dv: '#e5e5e5',
		cp: 'border-color',
		sel: '.comp.compPrompt ._sessionSidebar, .comp.compPrompt ._inputContainer',
		np: true,
	},

	// ─── Session sidebar ───
	{
		gn: 'Prompt Sidebar',
		dn: 'Sidebar Background',
		n: 'promptSidebarBackground',
		dv: '#f9f9f9',
		cp: 'background',
		sel: '.comp.compPrompt ._sessionSidebar',
		np: true,
	},
	{
		gn: 'Prompt Sidebar',
		dn: 'Session Hover Background',
		n: 'promptSessionHoverBackground',
		dv: '#f0f0f0',
		cp: 'background',
		sel: '.comp.compPrompt ._sessionItem:hover',
		np: true,
	},
	{
		gn: 'Prompt Sidebar',
		dn: 'Session Selected Background',
		n: 'promptSessionActiveBackground',
		dv: '#ececec',
		cp: 'background',
		sel: '.comp.compPrompt ._sessionItem._active',
		np: true,
	},

	// ─── Messages ───
	{
		gn: 'Prompt Messages',
		dn: 'User Message Background',
		n: 'promptUserMessageBackground',
		dv: '#f4f4f4',
		cp: 'background',
		sel: '.comp.compPrompt ._promptMessage._user',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'User Message Font Color',
		n: 'promptUserMessageFontColor',
		dv: '#1a1a1a',
		cp: 'color',
		sel: '.comp.compPrompt ._promptMessage._user',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'User Message Border Radius',
		n: 'promptUserMessageBorderRadius',
		dv: '20px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._promptMessage._user',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'Assistant Font Color',
		n: 'promptAssistantFontColor',
		dv: '#1a1a1a',
		cp: 'color',
		sel: '.comp.compPrompt ._assistantContent',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'Inline Code Background',
		n: 'promptInlineCodeBackground',
		dv: '#f4f4f4',
		cp: 'background',
		sel: '.comp.compPrompt ._assistantContent code',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'Code Block Background',
		n: 'promptCodeBlockBackground',
		dv: '#1e1e1e',
		cp: 'background',
		sel: '.comp.compPrompt ._assistantContent pre',
		np: true,
	},
	{
		gn: 'Prompt Messages',
		dn: 'Code Block Font Color',
		n: 'promptCodeBlockFontColor',
		dv: '#d4d4d4',
		cp: 'color',
		sel: '.comp.compPrompt ._assistantContent pre',
		np: true,
	},

	// ─── Composer ───
	{
		gn: 'Prompt Composer',
		dn: 'Input Background',
		n: 'promptInputBackground',
		dv: '#f4f4f4',
		cp: 'background',
		sel: '.comp.compPrompt ._inputContainer',
		np: true,
	},
	{
		gn: 'Prompt Composer',
		dn: 'Input Focus Background',
		n: 'promptInputFocusBackground',
		dv: '#ffffff',
		cp: 'background',
		sel: '.comp.compPrompt ._inputContainer:focus-within',
		np: true,
	},
	{
		gn: 'Prompt Composer',
		dn: 'Input Focus Border Color',
		n: 'promptInputFocusBorderColor',
		dv: '#d0d0d0',
		cp: 'border-color',
		sel: '.comp.compPrompt ._inputContainer:focus-within',
		np: true,
	},
	{
		gn: 'Prompt Composer',
		dn: 'Input Border Radius',
		n: 'promptInputBorderRadius',
		dv: '24px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._inputContainer',
		np: true,
	},
	{
		gn: 'Prompt Composer',
		dn: 'Input Font Color',
		n: 'promptInputFontColor',
		dv: '#1a1a1a',
		cp: 'color',
		sel: '.comp.compPrompt ._inputContainer textarea',
		np: true,
	},
	{
		gn: 'Prompt Composer',
		dn: 'Placeholder Font Color',
		n: 'promptPlaceholderFontColor',
		dv: '#9b9b9b',
		cp: 'color',
		sel: '.comp.compPrompt ._inputContainer textarea::placeholder',
		np: true,
	},

	// ─── Accent (send, active affordances) ───
	{
		gn: 'Prompt Accent',
		dn: 'Send Button Background',
		n: 'promptAccentBackground',
		dv: '#1a1a1a',
		cp: 'background',
		sel: '.comp.compPrompt ._sendButton',
		np: true,
	},
	{
		gn: 'Prompt Accent',
		dn: 'Send Button Font Color',
		n: 'promptAccentFontColor',
		dv: '#ffffff',
		cp: 'color',
		sel: '.comp.compPrompt ._sendButton',
		np: true,
	},
	{
		gn: 'Prompt Accent',
		dn: 'Send Button Disabled Background',
		n: 'promptAccentDisabledBackground',
		dv: '#d0d0d0',
		cp: 'background',
		sel: '.comp.compPrompt ._sendButton:disabled',
		np: true,
	},

	// ─── Shortcut hint (the key chip, shown while its modifier is held) ───
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Background',
		de: 'Background of the key chip shown while its modifier is held',
		n: 'promptShortcutHintBackground',
		dv: 'rgba(127, 127, 127, 0.22)',
		cp: 'background',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Font Color',
		n: 'promptShortcutHintFontColor',
		dv: 'inherit',
		cp: 'color',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Font Size',
		n: 'promptShortcutHintFontSize',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Padding',
		n: 'promptShortcutHintPadding',
		dv: '2px 5px',
		cp: 'padding',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Border Radius',
		n: 'promptShortcutHintBorderRadius',
		dv: '4px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},
	{
		gn: 'Shortcut Hint',
		dn: 'Shortcut Hint Margin',
		n: 'promptShortcutHintMargin',
		dv: '0px 4px 0px 0px',
		cp: 'margin',
		sel: '.comp.compPrompt ._inputActions ._shortcutHint',
		np: true,
	},

	// ─── Icons (composer + quick actions; send icon is Prompt Accent) ───
	// Each `dv` points at the text tone the icon is drawn in, so an app that sets
	// the tone recolors the icon with it, and one that sets neither keeps the
	// literal in PromptStyle.tsx (see Prompt Notices below for how that resolves).
	{
		gn: 'Prompt Icons',
		dn: 'Attachment Icon Color',
		n: 'promptAttachmentIconColor',
		dv: '<promptSecondaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._addAttachmentButton',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Attachment Icon Hover Color',
		n: 'promptAttachmentIconHoverColor',
		dv: '<promptFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._addAttachmentButton:hover',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Attachment Button Border Color',
		n: 'promptAttachmentBorderColor',
		dv: '#e0e0e0',
		cp: 'border-color',
		sel: '.comp.compPrompt ._addAttachmentButton',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Quick Action Icon Color',
		n: 'promptQuickActionIconColor',
		dv: '<promptSecondaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._quickActionIcon',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Microphone Icon Color',
		n: 'promptMicIconColor',
		dv: '<promptSecondaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._micButton',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Microphone Icon Hover Color',
		n: 'promptMicIconHoverColor',
		dv: '<promptFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._micButton:hover',
		np: true,
	},
	{
		gn: 'Prompt Icons',
		dn: 'Microphone Recording Color',
		n: 'promptMicRecordingColor',
		dv: '#ef4444',
		cp: 'color',
		sel: '.comp.compPrompt ._micButton._recording',
		np: true,
	},

	// ─── Agent activity card ───
	// Monochrome by design rule: state is carried by fill and motion, never hue.
	// Text colors default to the chat's text tones, so setting a tone recolors
	// the card with the rest of the chat; these keys tint just the card. Fills
	// and borders keep literal defaults, so the app-wide palette never tints it.
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Background',
		n: 'promptAgentCardBackground',
		dv: '#fbfbf9',
		cp: 'background',
		sel: '.comp.compPrompt ._agentCard',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Done Background',
		n: 'promptAgentCardDoneBackground',
		dv: '#ffffff',
		cp: 'background',
		sel: '.comp.compPrompt ._agentCard._done',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Border Color',
		n: 'promptAgentCardBorderColor',
		dv: '#ececec',
		cp: 'border-color',
		sel: '.comp.compPrompt ._agentCard',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Title Font Color',
		n: 'promptAgentCardTitleFontColor',
		dv: '<promptFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardTitle',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Time Chip Background',
		n: 'promptAgentCardTimeBackground',
		dv: '#f4f3ee',
		cp: 'background',
		sel: '.comp.compPrompt ._agentCardTime',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Time Chip Font Color',
		n: 'promptAgentCardTimeFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardTime',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Spinner Color',
		n: 'promptAgentSpinnerColor',
		dv: '#1a1a1a',
		cp: 'border-top-color',
		sel: '.comp.compPrompt ._agentCardSpin',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Check Background',
		n: 'promptAgentCheckBackground',
		dv: '#1a1a1a',
		cp: 'background',
		sel: '.comp.compPrompt ._agentCardCheck',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Card Check Font Color',
		n: 'promptAgentCheckFontColor',
		dv: '#ffffff',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardCheck',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Name Font Color',
		n: 'promptAgentNameFontColor',
		dv: '<promptFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentItemName',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Progress Font Color',
		de: 'The live doing-line under an agent name, and the settled outcome line',
		n: 'promptAgentDoingFontColor',
		dv: '<promptSecondaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentDoing',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Thinking Font Color',
		de: 'The streamed sub-agent reasoning quote',
		n: 'promptAgentThinkingFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentThink',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Agent Thinking Border Color',
		n: 'promptAgentThinkingBorderColor',
		dv: '#e2e1db',
		cp: 'border-left-color',
		sel: '.comp.compPrompt ._agentThink',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Tool Name Font Color',
		n: 'promptAgentToolNameFontColor',
		dv: '<promptFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardToolName',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Tool Progress Font Color',
		de: 'The in-place tool_update line while a tool runs',
		n: 'promptAgentToolLiveFontColor',
		dv: '<promptSecondaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardTool._live ._agentCardToolLive',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Tool Result Font Color',
		de: 'The settled summary line after tool_result',
		n: 'promptAgentToolResultFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardTool._settled ._agentCardToolLive',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Tool History Font Color',
		de: 'Past tool_update lines revealed by expanding a settled tool row',
		n: 'promptAgentToolHistoryFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._agentCardToolHistLine',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Status Dot Color',
		de: 'Filled blinking while running, filled steady when done',
		n: 'promptStatusDotColor',
		dv: '#1a1a1a',
		cp: 'background',
		sel: '.comp.compPrompt ._statusDot._running, .comp.compPrompt ._statusDot._success',
		np: true,
	},
	{
		gn: 'Prompt Agent Activity',
		dn: 'Status Dot Error Border Color',
		de: 'The hollow dot a failed agent or tool shows',
		n: 'promptStatusDotErrorBorderColor',
		dv: '#1a1a1a',
		cp: 'border-color',
		sel: '.comp.compPrompt ._statusDot._error',
		np: true,
	},

	// ─── Thinking (the orchestrator's own reasoning block) ───
	{
		gn: 'Prompt Thinking',
		dn: 'Thinking Label Font Color',
		n: 'promptThinkingLabelFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._thinkingLabel',
		np: true,
	},
	{
		gn: 'Prompt Thinking',
		dn: 'Thinking Reasoning Font Color',
		n: 'promptThinkingReasoningFontColor',
		dv: '<promptTertiaryFontColor>',
		cp: 'color',
		sel: '.comp.compPrompt ._thinkingReasoning',
		np: true,
	},
	{
		gn: 'Prompt Thinking',
		dn: 'Thinking Reasoning Border Color',
		n: 'promptThinkingReasoningBorderColor',
		dv: '#e2e1db',
		cp: 'border-left-color',
		sel: '.comp.compPrompt ._thinkingReasoning',
		np: true,
	},

	// ─── Notices, and the confirmation block ───
	//
	// These surfaces used to reach past this file for their colour and ask the
	// app-wide `colorTwo`, `accentTintColor` and `borderColorNine` directly.
	// That works in appbuilder, whose themes set `colorTwo` to the theme's
	// ACCENT INK -- #B45309 in amber, #0A55A8 in coolBlue -- and it breaks
	// anywhere that reads the same name the other way. sitezump sets `colorTwo`
	// to #F9FAFB, its page ground, which painted the draft bar's text in
	// near-white on a cream wash and made the confirmation icon invisible
	// outright.
	//
	// So each of them gets its own name here. Every `dv` below is `<theOldOne>`,
	// which keeps the behaviour identical for an app that set the old variable,
	// and resolves to nothing for an app that did not -- an empty declaration,
	// which the parser drops, leaving the literal in PromptStyle.tsx standing.
	// Either way nothing moves until an app sets the new name.
	{
		gn: 'Prompt Notices',
		dn: 'Notice Background',
		n: 'promptNoticeBackground',
		dv: '<accentTintColor>',
		cp: 'background-color',
		sel: '.comp.compPrompt ._promptSavedNotice, .comp.compPrompt ._promptPendingBar',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Notice Font Color',
		n: 'promptNoticeFontColor',
		dv: '<colorTwo>',
		cp: 'color',
		sel: '.comp.compPrompt ._promptSavedNotice, .comp.compPrompt ._promptPendingBar',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Notice Border Radius',
		n: 'promptNoticeBorderRadius',
		dv: '8px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._promptSavedNotice, .comp.compPrompt ._promptPendingBar',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Notice Button Border Color',
		n: 'promptNoticeButtonBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-color',
		sel: '.comp.compPrompt ._promptPendingBtn',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Notice Button Border Radius',
		n: 'promptNoticeButtonBorderRadius',
		dv: '999px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._promptPendingBtn',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Notice Button Hover Background',
		n: 'promptNoticeButtonHoverBackground',
		dv: '<accentTintColor>',
		cp: 'background-color',
		sel: '.comp.compPrompt ._promptPendingBtn:hover',
		np: true,
	},
	{
		gn: 'Prompt Notices',
		dn: 'Warning Font Color',
		n: 'promptWarningFontColor',
		dv: '<colorTwo>',
		cp: 'color',
		sel: '.comp.compPrompt ._usageContext._warning',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Confirmation Background',
		n: 'promptConfirmBackground',
		dv: '<surfaceColorOne>',
		cp: 'background',
		sel: '.comp.compPrompt ._confirmationPrompt',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Confirmation Border Color',
		n: 'promptConfirmBorderColor',
		dv: '<borderColorNine>',
		cp: 'border-color',
		sel: '.comp.compPrompt ._confirmationPrompt',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Confirmation Border Radius',
		n: 'promptConfirmBorderRadius',
		dv: '12px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._confirmationPrompt',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Confirmation Icon Color',
		n: 'promptConfirmIconColor',
		dv: '<colorTwo>',
		cp: 'color',
		sel: '.comp.compPrompt ._confirmationIcon',
		np: true,
	},
	{
		// The only one of these with no variable behind it at all: the approve
		// button is `background: #1a7f37` in PromptStyle.tsx, a literal, so no
		// theme could reach it however it was written.
		gn: 'Prompt Confirmation',
		dn: 'Approve Background',
		n: 'promptApproveBackground',
		dv: '#1a7f37',
		cp: 'background',
		sel: '.comp.compPrompt ._confirmationOption._approve',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Approve Border Color',
		n: 'promptApproveBorderColor',
		dv: '#1a7f37',
		cp: 'border-color',
		sel: '.comp.compPrompt ._confirmationOption._approve',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Approve Hover Background',
		n: 'promptApproveHoverBackground',
		dv: '#15692e',
		cp: 'background',
		sel: '.comp.compPrompt ._confirmationOption._approve:hover',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Approve Font Color',
		n: 'promptApproveFontColor',
		dv: '<colorSeven>',
		cp: 'color',
		sel: '.comp.compPrompt ._confirmationOption._approve',
		np: true,
	},
	{
		gn: 'Prompt Confirmation',
		dn: 'Confirmation Option Border Radius',
		n: 'promptConfirmOptionBorderRadius',
		dv: '8px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._confirmationOption',
		np: true,
	},

	// ─── Type scale ───
	// The chat's own sizes, so a theme with a tighter scale (mono's body is 14)
	// can bring the conversation in line with the rest of the app. Defaults are
	// the literals in PromptStyle.tsx.
	{
		gn: 'Prompt Type',
		dn: 'Welcome Title Font Size',
		n: 'promptEmptyTitleFontSize',
		dv: '24px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._emptyTitle',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Welcome Title Font Weight',
		n: 'promptEmptyTitleFontWeight',
		dv: '600',
		cp: 'font-weight',
		sel: '.comp.compPrompt ._emptyTitle',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Message Font Size',
		n: 'promptMessageFontSize',
		dv: '15px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._promptMessage._user, .comp.compPrompt ._assistantContent',
		np: true,
	},
	{
		// No default: the user bubble (1.6) and the reply (1.7) differ today.
		gn: 'Prompt Type',
		dn: 'Message Line Height',
		n: 'promptMessageLineHeight',
		cp: 'line-height',
		sel: '.comp.compPrompt ._promptMessage._user, .comp.compPrompt ._assistantContent',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Input Font Size',
		n: 'promptInputFontSize',
		dv: '15px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._inputContainer textarea',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Quick Action Font Size',
		n: 'promptQuickActionFontSize',
		dv: '14px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._quickActionItem',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Quick Action Pill Font Size',
		n: 'promptQuickActionPillFontSize',
		dv: '13px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._quickActions._pills ._quickActionItem',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Thinking Label Font Style',
		n: 'promptThinkingFontStyle',
		dv: 'italic',
		cp: 'font-style',
		sel: '.comp.compPrompt ._thinkingLabel',
		np: true,
	},
	{
		gn: 'Prompt Type',
		dn: 'Usage Bar Font Size',
		n: 'promptUsageFontSize',
		dv: '11px',
		cp: 'font-size',
		sel: '.comp.compPrompt ._usageBar',
		np: true,
	},

	// ─── Radii ───
	{
		gn: 'Prompt Radii',
		dn: 'Quick Action Border Radius',
		n: 'promptQuickActionBorderRadius',
		dv: '12px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._quickActionItem',
		np: true,
	},
	{
		gn: 'Prompt Radii',
		dn: 'Quick Action Pill Border Radius',
		n: 'promptQuickActionPillBorderRadius',
		dv: '20px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._quickActions._pills ._quickActionItem',
		np: true,
	},
	{
		gn: 'Prompt Radii',
		dn: 'Round Button Border Radius',
		n: 'promptRoundButtonBorderRadius',
		dv: '50%',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._sendButton, .comp.compPrompt ._addAttachmentButton',
		np: true,
	},
	{
		gn: 'Prompt Radii',
		dn: 'Thinking Block Border Radius',
		n: 'promptThinkingBorderRadius',
		dv: '12px',
		cp: 'border-radius',
		sel: '.comp.compPrompt ._thinkingBlock',
		np: true,
	},

	// ─── Markdown in replies ───
	// A reply is rendered by MarkdownParser, whose only table rule is the
	// app-wide `._markdown ._th, ._td { border: 1px solid #000; padding: 5px }`
	// and whose headings are the browser's (h1 = 2em bold). These are scoped to
	// the reply, so a theme can set them without touching Markdown elsewhere.
	// Border and padding default to the app-wide values; the rest have no
	// default and emit nothing until a theme sets them.
	{
		gn: 'Prompt Markdown',
		dn: 'Table Border Color',
		n: 'promptTableBorderColor',
		dv: '#000',
		cp: 'border-color',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._th, .comp.compPrompt ._assistantContent ._markdown ._td',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Table Cell Padding',
		n: 'promptTableCellPadding',
		dv: '5px',
		cp: 'padding',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._th, .comp.compPrompt ._assistantContent ._markdown ._td',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Table Font Size',
		n: 'promptTableFontSize',
		cp: 'font-size',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._table',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Table Header Background',
		n: 'promptTableHeaderBackground',
		cp: 'background',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._th',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Table Header Font Weight',
		n: 'promptTableHeaderFontWeight',
		cp: 'font-weight',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._th',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Heading 1 Font Size',
		n: 'promptHeadingOneFontSize',
		cp: 'font-size',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._h1',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Heading 2 Font Size',
		n: 'promptHeadingTwoFontSize',
		cp: 'font-size',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._h2',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Heading 3 To 6 Font Size',
		n: 'promptHeadingSmallFontSize',
		cp: 'font-size',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._h3, .comp.compPrompt ._assistantContent ._markdown ._h4, .comp.compPrompt ._assistantContent ._markdown ._h5, .comp.compPrompt ._assistantContent ._markdown ._h6',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Heading Font Weight',
		n: 'promptHeadingFontWeight',
		cp: 'font-weight',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._h1, .comp.compPrompt ._assistantContent ._markdown ._h2, .comp.compPrompt ._assistantContent ._markdown ._h3, .comp.compPrompt ._assistantContent ._markdown ._h4, .comp.compPrompt ._assistantContent ._markdown ._h5, .comp.compPrompt ._assistantContent ._markdown ._h6',
		np: true,
	},
	{
		gn: 'Prompt Markdown',
		dn: 'Heading Margin',
		n: 'promptHeadingMargin',
		cp: 'margin',
		sel: '.comp.compPrompt ._assistantContent ._markdown ._h1, .comp.compPrompt ._assistantContent ._markdown ._h2, .comp.compPrompt ._assistantContent ._markdown ._h3, .comp.compPrompt ._assistantContent ._markdown ._h4, .comp.compPrompt ._assistantContent ._markdown ._h5, .comp.compPrompt ._assistantContent ._markdown ._h6',
		np: true,
	},
];

export const styleDefaults = new Map<string, string>(
	styleProperties.filter(e => !!e.dv).map(({ n, dv }) => [n, dv!]),
);

export const stylePropertiesForTheme: Array<StylePropertyDefinition> = styleProperties;
